import { Archive, FileDown, Loader2, Star } from "lucide-react";
import React from "react";
import { useSelector } from "react-redux";
import { Constants } from "src/constant";
import { canWriteService, ServiceDomain } from "src/lib/roles";
import { selectUserProfile } from "src/store/authSlice";
import type { CarouselPublicationListItem } from "src/types/carouselPublication";
import { PastCarouselCard } from "./PastCarouselCard";

interface PastCarouselsPanelProps {
  publications: CarouselPublicationListItem[];
  loading: boolean;
  starredOnly: boolean;
  busyId: string | null;
  onToggleStarredOnly: () => void;
  onPreview: (pub: CarouselPublicationListItem) => void;
  onRepublish: (pub: CarouselPublicationListItem) => void;
  onToggleStar: (pub: CarouselPublicationListItem) => void;
  onDelete: (pub: CarouselPublicationListItem) => void;
  onExport: () => void;
  exporting?: boolean;
  exportError?: string | null;
}

export const PastCarouselsPanel: React.FC<PastCarouselsPanelProps> = ({
  publications,
  loading,
  starredOnly,
  busyId,
  onToggleStarredOnly,
  onPreview,
  onRepublish,
  onToggleStar,
  onDelete,
  onExport,
  exporting = false,
  exportError = null,
}) => {
  const userProfile = useSelector(selectUserProfile);
  const canWriteHappeningNow =
    userProfile != null &&
    canWriteService(userProfile.role, ServiceDomain.HappeningNow);

  const loadingComponent = (
    <div className="flex flex-col items-center justify-center rounded-2xl border-4 border-white/10 bg-white/5 py-10">
      <Loader2 className="h-9 w-9 animate-spin text-[#6366F1]" />
      <p className="mt-3 text-sm font-extrabold text-white/70">
        Loading history…
      </p>
    </div>
  );

  const emptyComponent = (
    <div className="rounded-2xl border-4 border-dashed border-white/25 bg-white/5 p-6 text-center">
      <p className="text-base font-extrabold text-white/90">
        {starredOnly ? "No starred carousels yet" : "No past carousels yet"}
      </p>
      <p className="mt-1 text-sm font-bold text-white/60">
        {starredOnly
          ? "Click the star on a past carousel to keep it here."
          : "Click Publish carousel above and your first snapshot will appear here."}
      </p>
    </div>
  );

  return (
    <section
      aria-labelledby="past-carousels-heading"
      className="mt-10 flex max-h-[calc(100dvh-8rem)] min-h-0 flex-col overflow-hidden rounded-[1.75rem] border-4 border-white/10 bg-white/5 backdrop-blur sm:max-h-[calc(100dvh-6rem)]"
    >
      <div className="shrink-0 border-b border-white/10 bg-white/5 p-5 backdrop-blur-sm sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#6366F1] text-white shadow-[0_3px_0_#4338CA]">
              <Archive className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <h2
                id="past-carousels-heading"
                className="text-lg font-extrabold text-white sm:text-xl"
              >
                Past carousels
              </h2>
              <p className="text-xs font-bold text-white/70 sm:text-sm">
                {starredOnly
                  ? Constants.STARRED_ONLY_DESCRIPTION
                  : `Last ${Constants.CAROUSEL_HISTORY_DEFAULT_DAYS} days. Star a carousel to keep it past auto-cleanup.`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onToggleStarredOnly}
              className={[
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-extrabold uppercase tracking-wide ring-2",
                starredOnly
                  ? "bg-[#FEF3C7] text-[#92400E] ring-[#F59E0B]/40"
                  : "bg-white/10 text-white/90 ring-white/20",
              ].join(" ")}
              aria-pressed={starredOnly}
            >
              <Star
                className={`h-3.5 w-3.5 ${starredOnly ? "fill-current" : ""}`}
                aria-hidden
              />
              Starred only
            </button>
            {canWriteHappeningNow && (
              <button
                type="button"
                onClick={onExport}
                disabled={exporting || loading || publications.length === 0}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-extrabold uppercase tracking-wide ring-2 bg-white/10 text-white/90 ring-white/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {exporting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <FileDown className="h-3.5 w-3.5" aria-hidden />
                )}
                {exporting ? "Exporting…" : "Export"}
              </button>
            )}
          </div>
        </div>

        {exportError ? (
          <p className="mt-4 rounded-xl border-2 border-[#EF4444] bg-[#FEE2E2] px-4 py-2 text-center text-sm font-extrabold text-[#B91C1C]">
            {exportError}
          </p>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
        {loading ? (
          loadingComponent
        ) : publications.length === 0 ? (
          emptyComponent
        ) : (
          <div className="flex flex-col gap-2">
            {publications.map((pub) => (
              <PastCarouselCard
                key={pub.id}
                publication={pub}
                busy={busyId === pub.id}
                onPreview={onPreview}
                onRepublish={onRepublish}
                onToggleStar={onToggleStar}
                onDelete={onDelete}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
